import moment from 'moment';

const TIME_FORMAT = 'h:mm A';

export const toMinutes = (value) => {
  if (value == null) return null;

  if (typeof value === 'number') {
    const asMoment = moment.utc(value * 3600 * 1000);
    return asMoment.hours() * 60 + asMoment.minutes();
  }

  const parsed = moment(value, TIME_FORMAT);
  if (!parsed.isValid()) return null;
  return parsed.hours() * 60 + parsed.minutes();
};

export const mergeIntervals = (intervals) => {
  if (!intervals.length) return [];

  const sorted = [...intervals].sort((a, b) => a[0] - b[0]);
  const merged = [];

  let [currentStart, currentEnd] = sorted[0];

  for (let i = 1; i < sorted.length; i += 1) {
    const [start, end] = sorted[i];
    if (start <= currentEnd) {
      currentEnd = Math.max(currentEnd, end);
    } else {
      merged.push([currentStart, currentEnd]);
      currentStart = start;
      currentEnd = end;
    }
  }

  merged.push([currentStart, currentEnd]);
  return merged;
};

const buildWorkingIntervals = (day) => {
  if (!day) return [];

  const intervals = [];

  const addInterval = (range) => {
    if (!range) return;
    const start = toMinutes(range.starts_at);
    const end = toMinutes(range.ends_at);

    if (start == null && end == null) return;
    if (start == null || end == null) return;
    if (end <= start) return;

    intervals.push([start, end]);
  };

  addInterval(day.location);
  addInterval(day.virtual);

  return intervals;
};

const buildBreakIntervals = (day) => {
  if (!day || !Array.isArray(day.break) || !day.break.length) return [];

  const intervals = [];

  day.break.forEach((b) => {
    const start = toMinutes(b.starts_at);
    const end = toMinutes(b.ends_at);

    if (start == null && end == null) return;
    if (start == null || end == null) return;
    if (end <= start) return;

    intervals.push([start, end]);
  });

  return intervals;
};

export const calculateDayTotalMinutes = (day) => {
  const workingIntervals = mergeIntervals(buildWorkingIntervals(day));
  if (!workingIntervals.length) return 0;

  let totalMinutes = workingIntervals.reduce((sum, [start, end]) => sum + (end - start), 0);

  const rawBreaks = buildBreakIntervals(day);
  if (!rawBreaks.length) return totalMinutes;

  // Only subtract portions of breaks that fall inside working intervals
  const inWorkingBreaks = [];

  rawBreaks.forEach(([bStart, bEnd]) => {
    workingIntervals.forEach(([wStart, wEnd]) => {
      const start = Math.max(wStart, bStart);
      const end = Math.min(wEnd, bEnd);
      if (end > start) {
        inWorkingBreaks.push([start, end]);
      }
    });
  });

  const mergedBreaks = mergeIntervals(inWorkingBreaks);

  mergedBreaks.forEach(([start, end]) => {
    totalMinutes -= end - start;
  });

  return Math.max(0, totalMinutes);
};

export const isDayAvailable = (day) => buildWorkingIntervals(day).length > 0;

export const validateAvailableTimes = (availableTimes = []) => {
  const errors = {};
  let hasError = false;

  availableTimes.forEach((day) => {
    const dayErrors = {};
    const workingIntervals = buildWorkingIntervals(day);

    const validateRange = (range, key) => {
      if (!range) return;
      const startRaw = range.starts_at;
      const endRaw = range.ends_at;

      if (!startRaw && !endRaw) return;
      if (!startRaw || !endRaw) {
        dayErrors[key] = 'Please select both start and end time.';
        return;
      }

      const start = toMinutes(startRaw);
      const end = toMinutes(endRaw);

      if (start == null || end == null) {
        dayErrors[key] = 'Invalid time value.';
        return;
      }

      if (end <= start) {
        dayErrors[key] = 'End time must be later than start time.';
      }
    };

    validateRange(day.location, 'location');
    validateRange(day.virtual, 'virtual');

    if (Array.isArray(day.break) && day.break.length) {
      const breakErrors = {};
      const breaks = [];

      day.break.forEach((b) => {
        const startRaw = b.starts_at;
        const endRaw = b.ends_at;

        if (!startRaw && !endRaw) return;
        if (!startRaw || !endRaw) {
          breakErrors[b.id] = 'Please select both start and end time for the break.';
          return;
        }

        const start = toMinutes(startRaw);
        const end = toMinutes(endRaw);

        if (start == null || end == null || end <= start) {
          breakErrors[b.id] = 'Break end time must be later than start time.';
          return;
        }

        breaks.push({ id: b.id, start, end });
      });

      if (breaks.length && !workingIntervals.length) {
        breaks.forEach((b) => {
          breakErrors[b.id] = 'Add working hours before defining breaks.';
        });
      } else if (breaks.length && workingIntervals.length) {
        // Ensure breaks fall inside working hours
        breaks.forEach((b) => {
          const inside = workingIntervals.some(
            ([wStart, wEnd]) => b.start >= wStart && b.end <= wEnd
          );
          if (!inside) {
            breakErrors[b.id] = 'Break must fall within working hours.';
          }
        });

        // Ensure breaks do not overlap each other
        const sorted = [...breaks].sort((a, b) => a.start - b.start);
        for (let i = 1; i < sorted.length; i += 1) {
          if (sorted[i].start < sorted[i - 1].end) {
            breakErrors[sorted[i].id] = 'Breaks cannot overlap.';
            breakErrors[sorted[i - 1].id] = 'Breaks cannot overlap.';
          }
        }
      }

      if (Object.keys(breakErrors).length) {
        dayErrors.breaks = breakErrors;
      }
    }

    if (Object.keys(dayErrors).length) {
      hasError = true;
      errors[day.day] = dayErrors;
    }
  });

  return { hasError, errors };
};
